import React, { lazy } from 'react';
import {
  Route,
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';

import Layout from './components/Layout';
import LazyRoute from './components/LazyRoute';
import { enableAppleWalletIdCards } from './hooks/useGetFeatureFlags';
import AutoPayPage from './pages/AutoPay';
import AutoPayPaymentMethodPage from './pages/AutoPay/components/AutoPayPaymentMethod';
import AutoPayPaymentMethodActions from './pages/AutoPay/components/AutoPayPaymentMethodActions';
import PolicySelection from './pages/Claims/GlassClaim/PolicySelection';
import LocalSetNewToken from './pages/LocalSetNewToken';
import Maintenance from './pages/Maintenance';
import MultiplePolicyPayments from './pages/MultiplePolicyPayments';
import SelectedPaymentsPage from './pages/MultiplePolicyPayments/components/SelectedPaymentsPage';
import Overview from './pages/Overview';
import Payment from './pages/Payment';
import AddPaymentMethodPage from './pages/Payment/AddPaymentMethod';
import EditPaymentMethodActions from './pages/PaymentMethod/EditPaymentMethodActions';
import PaymentMethodPage from './pages/PaymentMethod/PaymentMethod';
import PaymentReviewPage from './pages/PaymentReview';
import PaymentStatusPage from './pages/PaymentStatus';
import PolicyDetails from './pages/PolicyDetails';
import AuthLayout from './pages/RegistrationAndSignIn/AuthLayout';
import EligibilityCheck from './pages/RegistrationAndSignIn/Club/OrganicRegistration/EligibilityCheck';
import Login from './pages/RegistrationAndSignIn/DirectAccess/Login';
import Logout from './pages/RegistrationAndSignIn/DirectAccess/Logout';
import OrganicRegistration from './pages/RegistrationAndSignIn/DirectAccess/OrganicRegistration';
import EmailAndPasswordForm from './pages/RegistrationAndSignIn/DirectAccess/OrganicRegistration/EmailAndPasswordForm';
import EmailVerification from './pages/RegistrationAndSignIn/DirectAccess/OrganicRegistration/EmailVerification';
import OneTimeVerificationCodeFlow from './pages/RegistrationAndSignIn/DirectAccess/OrganicRegistration/OneTimeVerificationCodeFlow';
import OneTimeEmailVerification from './pages/RegistrationAndSignIn/DirectAccess/OrganicRegistration/OneTimeVerificationCodeFlow/OneTimeEmailVerification';
import Register from './pages/RegistrationAndSignIn/DirectAccess/OrganicRegistration/OneTimeVerificationCodeFlow/Register';
import SmsVerification from './pages/RegistrationAndSignIn/DirectAccess/OrganicRegistration/OneTimeVerificationCodeFlow/SmsVerification';
import VerificationMethod from './pages/RegistrationAndSignIn/DirectAccess/OrganicRegistration/OneTimeVerificationCodeFlow/VerificationMethod';
import PolicyDetailsForm from './pages/RegistrationAndSignIn/DirectAccess/OrganicRegistration/PolicyDetailsForm';
import ZipCodeCheck from './pages/RegistrationAndSignIn/ZipCodeCheck';

import LoginSso from './pages/RegistrationAndSignIn/Club/Login';
import LogoutSso from './pages/RegistrationAndSignIn/Club/Logout';
import OrganicRegistrationSso from './pages/RegistrationAndSignIn/Club/OrganicRegistration';
import EmailVerificationSso from './pages/RegistrationAndSignIn/Club/OrganicRegistration/EmailVerification';
import OneTimeVerificationCodeFlowSso from './pages/RegistrationAndSignIn/Club/OrganicRegistration/OneTimeVerificationCodeFlow';
import OneTimeEmailVerificationSso from './pages/RegistrationAndSignIn/Club/OrganicRegistration/OneTimeVerificationCodeFlow/ClubEmailVerification';
import RegisterSso from './pages/RegistrationAndSignIn/Club/OrganicRegistration/OneTimeVerificationCodeFlow/ClubSsoRegister';
import SmsVerificationSso from './pages/RegistrationAndSignIn/Club/OrganicRegistration/OneTimeVerificationCodeFlow/ClubSmsVerification';
import VerificationMethodSso from './pages/RegistrationAndSignIn/Club/OrganicRegistration/OneTimeVerificationCodeFlow/ClubSsoVerificationMethod';
import PolicyDetailsFormSso from './pages/RegistrationAndSignIn/Club/OrganicRegistration/PolicyDetailsForm';

import WalletPage from './pages/Wallet';
import WalletPaymentMethodActions from './pages/Wallet/PaymentMethodActions';
import Welcome from './pages/Welcome';

const ContactUs = lazy(() => import('./pages/ContactUs'));
const DocumentLibraryPage = lazy(
  () => import('./pages/Documents/DocumentLibrary')
);
const Endorsement = lazy(() => import('./pages/Endorsement'));
const FAQ = lazy(() => import('./pages/FAQ'));
const IdCardsPage = lazy(() => import('./pages/IdCards'));
const PreferencesPage = lazy(
  () => import('./pages/Preferences/PreferencesPage')
);
const ChangePasswordPage = lazy(
  () => import('./pages/ChangePassword/ChangePasswordPage')
);
const TransactionHistory = lazy(() => import('./pages/TransactionHistory'));
const SsoConversionPage = lazy(
  () => import('./pages/RegistrationAndSignIn/DirectAccess/SsoConversion')
);
const ResetPassword = lazy(
  () => import('./pages/RegistrationAndSignIn/DirectAccess/ResetPassword')
);
const SimplifiedRegistration = lazy(
  () =>
    import('./pages/RegistrationAndSignIn/DirectAccess/SimplifiedRegistration')
);

// Claims forms
const ClaimsDetails = lazy(() => import('./pages/ClaimDetails'));
const AutoClaim = lazy(() => import('./pages/Claims/AutoClaim'));
const AutoClaimDriverDetails = lazy(
  () => import('./pages/Claims/AutoClaim/AutoClaimDriverDetails')
);
const AutoClaimIncidentDetails = lazy(
  () => import('./pages/Claims/AutoClaim/AutoClaimIncidentDetails')
);
const AutoClaimVehicleDetails = lazy(
  () => import('./pages/Claims/AutoClaim/AutoClaimVehicleDetails')
);
const RepairShop = lazy(() => import('./pages/Claims/AutoClaim/RepairShop'));
const GlassClaim = lazy(() => import('./pages/Claims/GlassClaim/GlassClaim'));
const GlassClaimDetailsPage = lazy(
  () => import('./pages/Claims/GlassClaim/GlassClaimDetails')
);
const GlassClaimDriverInfoPage = lazy(
  () => import('./pages/Claims/GlassClaim/GlassClaimDriverInfo')
);

const SimplifiedRegistrationSso = lazy(
  () => import('./pages/RegistrationAndSignIn/Club/SimplifiedRegistration')
);

const PropertyClaim = lazy(() => import('./pages/Claims/PropertyClaim'));
const PropertyClaimContactDetails = lazy(
  () => import('./pages/Claims/PropertyClaim/PropertyClaimContactDetails')
);
const PropertyClaimPropertyDetails = lazy(
  () => import('./pages/Claims/PropertyClaim/PropertyClaimPropertyDetails')
);
const PropertyClaimIncidentDetails = lazy(
  () => import('./pages/Claims/PropertyClaim/PropertyClaimIncidentDetails')
);
const PropertyClaimRepairInformation = lazy(
  () => import('./pages/Claims/PropertyClaim/PropertyClaimRepairInformation')
);

const isLocal = window.location.hostname.includes('localhost');

const enableNewSso = import.meta.env.VITE_ENABLE_SSO === 'true';

const router = createBrowserRouter(
  createRoutesFromElements(
    import.meta.env.VITE_SHOW_MAINTENANCE_PAGE === 'true' ? (
      <Route element={<AuthLayout />}>
        <Route index element={<Maintenance />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    ) : (
      <>
        {isLocal ? (
          <Route path="set-token" element={<LocalSetNewToken />} />
        ) : (
          <Route path="set-token" element={<Navigate to="/" replace />} />
        )}
        <Route element={<AuthLayout />}>
          {enableNewSso && (
            <Route path="club">
              <Route path="login" element={<LoginSso />} />
              <Route path="logout" element={<LogoutSso />} />
              <Route path="register" element={<OrganicRegistrationSso />}>
                <Route index element={<EligibilityCheck />} />
                <Route path="policy" element={<PolicyDetailsFormSso />} />
                <Route path="verification" element={<EmailVerificationSso />} />
                <Route path="code" element={<OneTimeVerificationCodeFlowSso />}>
                  <Route index element={<VerificationMethodSso />} />
                  <Route
                    path="email"
                    element={<OneTimeEmailVerificationSso />}
                  />
                  <Route path="sms" element={<SmsVerificationSso />} />
                  <Route path="verification" element={<RegisterSso />} />
                </Route>
              </Route>
              <Route
                path="simplified-registration/:dcmId"
                element={<SimplifiedRegistrationSso />}
              />
            </Route>
          )}
          <Route
            path="/conversion"
            element={
              <LazyRoute>
                <SsoConversionPage />
              </LazyRoute>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route
            path="/reset"
            element={
              <LazyRoute>
                <ResetPassword />
              </LazyRoute>
            }
          />
          <Route path="/register" element={<OrganicRegistration />}>
            <Route index element={<EmailAndPasswordForm />} />
            <Route path="policy" element={<PolicyDetailsForm />} />
            <Route path="verification" element={<EmailVerification />} />
            <Route path="code" element={<OneTimeVerificationCodeFlow />}>
              <Route index element={<VerificationMethod />} />
              <Route path="email" element={<OneTimeEmailVerification />} />
              <Route path="sms" element={<SmsVerification />} />
              <Route path="verification" element={<Register />} />
            </Route>
          </Route>
          <Route
            path="/simplified-registration/:dcmId"
            element={
              <LazyRoute>
                <SimplifiedRegistration />
              </LazyRoute>
            }
          />
          <Route path="/policy-zip" element={<ZipCodeCheck />} />
          <Route path="/logout" element={<Logout />} />
        </Route>
        <Route element={<Layout />}>
          <Route index element={<Welcome />} />
          <Route path="/bills" element={<Overview currentTab="bills" />} />
          <Route path="/claims" element={<Overview currentTab="claims" />} />
          <Route
            path="/claims/:claimNumber"
            element={
              <LazyRoute>
                <ClaimsDetails />
              </LazyRoute>
            }
          />
          <Route
            element={
              <LazyRoute>
                <GlassClaim />
              </LazyRoute>
            }
          >
            <Route path="/claims/glass" element={<PolicySelection />} />
            <Route
              path="/claims/glass/details"
              element={
                <LazyRoute>
                  <GlassClaimDetailsPage />
                </LazyRoute>
              }
            />
            <Route
              path="/claims/glass/driver-info"
              element={
                <LazyRoute>
                  <GlassClaimDriverInfoPage />
                </LazyRoute>
              }
            />
          </Route>
          <Route
            path="/claims/auto"
            element={
              <LazyRoute>
                <AutoClaim />
              </LazyRoute>
            }
          >
            <Route
              index
              element={
                <LazyRoute>
                  <AutoClaimDriverDetails />
                </LazyRoute>
              }
            />
            <Route
              path="/claims/auto/vehicle"
              element={
                <LazyRoute>
                  <AutoClaimVehicleDetails />
                </LazyRoute>
              }
            />
            <Route
              path="/claims/auto/incident"
              element={
                <LazyRoute>
                  <AutoClaimIncidentDetails />
                </LazyRoute>
              }
            />
            <Route
              path="/claims/auto/repair"
              element={
                <LazyRoute>
                  <RepairShop />
                </LazyRoute>
              }
            />
          </Route>
          <Route
            path="/claims/property"
            element={
              <LazyRoute>
                <PropertyClaim />
              </LazyRoute>
            }
          >
            <Route
              index
              element={
                <LazyRoute>
                  <PropertyClaimContactDetails />
                </LazyRoute>
              }
            />
            <Route
              path="property-details"
              element={
                <LazyRoute>
                  <PropertyClaimPropertyDetails />
                </LazyRoute>
              }
            />
            <Route
              path="incident"
              element={
                <LazyRoute>
                  <PropertyClaimIncidentDetails />
                </LazyRoute>
              }
            />
            <Route
              path="repair-information"
              element={
                <LazyRoute>
                  <PropertyClaimRepairInformation />
                </LazyRoute>
              }
            />
          </Route>
          <Route
            path="/contact-us"
            element={
              <LazyRoute>
                <ContactUs />
              </LazyRoute>
            }
          />
          <Route
            path="/help"
            element={
              <LazyRoute>
                <FAQ />
              </LazyRoute>
            }
          />
          <Route
            path="/policies"
            element={<Overview currentTab="policies" />}
          />
          <Route
            path="/documents"
            element={
              <LazyRoute>
                <DocumentLibraryPage />
              </LazyRoute>
            }
          />
          <Route
            path="/documents/:policyNumber"
            element={
              <LazyRoute>
                <DocumentLibraryPage />
              </LazyRoute>
            }
          />
          {enableAppleWalletIdCards && (
            <Route
              path="/id-cards"
              element={
                <LazyRoute>
                  <IdCardsPage />
                </LazyRoute>
              }
            />
          )}
          {/* The following routes are fallbacks for the legacy PBE component. It
          hard codes the redirects so we do this to make sure the user lands on
          the expected page */}
          <Route
            path="/autoPolicyDetails"
            element={<Navigate to="/policies" replace />}
          />
          <Route
            path="/edit/autoPolicyDetails"
            element={<Navigate to="/policies" replace />}
          />
          {/* End fallback PBE routes */}
          <Route
            path="/edit/:policyNumber"
            element={
              <LazyRoute>
                <Endorsement />
              </LazyRoute>
            }
          />
          <Route
            path="/transaction-history"
            element={
              <LazyRoute>
                <TransactionHistory />
              </LazyRoute>
            }
          />
          <Route path="/payment" element={<Payment />} />
          <Route path="/payment/method" element={<PaymentMethodPage />} />
          <Route
            path="/payment/method/edit"
            element={<EditPaymentMethodActions />}
          />
          <Route
            path="/payment/method/add"
            element={<AddPaymentMethodPage />}
          />
          <Route path="/payment/review" element={<PaymentReviewPage />} />
          <Route path="/payment/status" element={<PaymentStatusPage />} />
          <Route path="/wallet" element={<WalletPage />} />
          <Route path="/wallet/edit" element={<WalletPaymentMethodActions />} />
          <Route path="/wallet/add" element={<WalletPaymentMethodActions />} />
          <Route path="/autopay" element={<AutoPayPage />} />
          <Route
            path="/autopay/method"
            element={<AutoPayPaymentMethodPage />}
          />
          <Route
            path="/autopay/method/add"
            element={<AutoPayPaymentMethodActions />}
          />
          <Route
            path="/autopay/method/edit"
            element={<AutoPayPaymentMethodActions />}
          />
          <Route
            path="/multiple-payment"
            element={<MultiplePolicyPayments />}
          />
          <Route
            path="/multiple-payment/selected-payments"
            element={<SelectedPaymentsPage />}
          />
          <Route path="/policy/:policyNumber" element={<PolicyDetails />} />
          <Route
            path="/preferences"
            element={
              <LazyRoute>
                <PreferencesPage />
              </LazyRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <LazyRoute>
                <ChangePasswordPage />
              </LazyRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </>
    )
  )
);

export default router;
